import type { SessionId } from './types';

/**
 * Unvalidated workflow JSON from the Tauri IPC bridge before Zod parsing.
 * ml-scribe may return a JSON string or a parsed object; primitives and arrays
 * are rejected at parse time.
 */
export type WorkflowWirePayload = string | Record<string, unknown>;

/** Narrows poll/API `unknown` workflow data to the wire shape accepted by the parser. */
export function coerceWorkflowWirePayload(value: unknown): WorkflowWirePayload {
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
}

export type UploadStage =
  | 'pending'
  | 'pre-zipped'
  | 'uploading'
  | 'polling'
  | 'ready'
  | 'failed';

export type UploadFailureKind = 'upload' | 'generation';

export interface RecorderUploadSidecar {
  readonly stage: UploadStage;
  readonly recordingId: string | null;
  readonly idempotencyKey: string;
  readonly recordingName?: string | null;
  readonly uploadAttempts: number;
  readonly lastAttemptAt: string | null;
  readonly failureKind: UploadFailureKind | null;
  readonly errorCode: string | null;
  readonly errorMessage: string | null;
}

export interface RecorderUploadArchive {
  readonly path: string;
  readonly fileName: string;
  readonly mimeType: string;
}

export interface RecorderUploadPreparation {
  readonly state: RecorderUploadSidecar;
  readonly archive: RecorderUploadArchive | null;
}

export interface RecorderUploadAuthContext {
  readonly uploadUrl: string;
  readonly bearerToken: string | null;
  readonly workspaceId: string | null;
  readonly tempUserId: string | null;
  readonly clientId: string;
  /**
   * Locked for this upload from the FE flag. Omit → legacy behaviour.
   */
  readonly rustAuth0TokenRefresh: boolean;
}

export type RecorderUploadMetadata = Omit<RecorderUploadAuthContext, 'bearerToken'>;

export interface RecorderPerformUploadOutcome {
  readonly recordingId: string;
  readonly state: RecorderUploadSidecar;
}

export interface RecorderUploadStateChangedPayload {
  readonly sessionId: SessionId;
  readonly stage: UploadStage;
  readonly uploadAttempts: number;
  readonly recordingId?: string;
  readonly failureKind?: UploadFailureKind;
  readonly recordingName?: string;
  readonly errorCode?: RecorderUploaderErrorCode;
  readonly errorMessage?: string;
}

export interface RecorderUploadProgressPayload {
  readonly sessionId: SessionId;
  readonly bytesUploaded: number;
  readonly bytesTotal: number;
  readonly partsCompleted: number;
  readonly partsTotal: number;
}

export interface RecorderAnalysisStatusChangedPayload {
  readonly sessionId: SessionId;
  readonly recordingId: string;
  readonly status: 'processing' | 'ready' | 'failed';
  readonly errorCode?: string;
  readonly errorMessage?: string;
}

export type RecorderUploaderResult =
  | { readonly ok: true; readonly state: RecorderUploadSidecar }
  | { readonly ok: false; readonly error: RecorderUploaderError };

export type RecorderUploadPreparationResult =
  | { readonly ok: true; readonly preparation: RecorderUploadPreparation }
  | { readonly ok: false; readonly error: RecorderUploaderError };

export type RecorderUploadStateListener = (
  payload: RecorderUploadStateChangedPayload
) => void;

export type RecorderUploadProgressListener = (
  payload: RecorderUploadProgressPayload
) => void;

export type RecorderAnalysisStatusListener = (
  payload: RecorderAnalysisStatusChangedPayload
) => void;

export type RecorderUploadUnsubscribe = () => void;

export type RecorderUploaderErrorCode =
  | 'UPLOAD_AUTH_UNAVAILABLE'
  | 'UPLOAD_SESSION_NOT_FOUND'
  | 'UPLOAD_STATE_NOT_FOUND'
  | 'UPLOAD_INVALID_STAGE'
  | 'UPLOAD_ARCHIVE_FAILED'
  | 'UPLOAD_NETWORK_ERROR'
  | 'UPLOAD_SERVER_ERROR'
  | 'UPLOAD_CLIENT_ERROR'
  | 'UPLOAD_FORBIDDEN'
  | 'UPLOAD_INTERRUPTED'
  | 'WORKFLOW_NOT_READY'
  | 'WORKFLOW_NOT_FOUND'
  | 'WORKFLOW_WRITE_FAILED'
  | 'GENERATION_FAILED'
  | 'UNKNOWN';

export type RecorderUploadOutcome =
  | {
      readonly kind: 'analysis-ready';
      readonly recordingId: string;
      readonly workflowData: WorkflowWirePayload;
    }
  | {
      readonly kind: 'analysis-failed';
      readonly recordingId: string;
      readonly errorCode?: RecorderUploaderErrorCode;
      readonly errorMessage?: string;
    };

export class RecorderUploaderError extends Error {
  readonly code: RecorderUploaderErrorCode;
  override readonly cause?: unknown;

  constructor(code: RecorderUploaderErrorCode, options?: { cause?: unknown }) {
    super(code);
    this.name = 'RecorderUploaderError';
    this.code = code;
    this.cause = options?.cause;
  }
}
